import { AiSearchResponse } from 'src/app/models/ai-search';
import { DiscoverFeaturedCollection, discoverImageUrl } from './discover-landing.data';

// The featured block ("A … collection. / A hundred possibilities.") shows either one of the
// bundled collections or what the search returned, in one shape. Copy below is a draft for the
// designer (owner, 2026-09-18: search → 7 previews → "Explore all N" → sign-in).

export type FeaturedStatus = 'ready' | 'loading' | 'empty' | 'error';

export interface FeaturedCard {
  src: string;
  alt: string;
}

export interface FeaturedBlock {
  // trackBy key: a new key re-creates the block (and runs its fade-in), the same key updates
  // it in place — that is how the skeletons turn into cards without a second animation.
  key: string;
  // Collection name for the request dialog's context; '' for a search.
  name: string;
  // What "Explore all" / the cards hand to /library?q=….
  query: string;
  headline: string;
  subline: string;
  aside: string;
  // "30", or "200+" when the backend's limit was hit.
  countLabel: string;
  cards: FeaturedCard[];
  status: FeaturedStatus;
}

// One row on the 1920 frame; tablets wrap to 4 + 3, phones show six.
export const FEATURED_CARD_COUNT = 7;
// The backend clamps the limit at 200, so at 200 the real total is unknown.
export const DISCOVER_SEARCH_LIMIT = 200;
// The desktop headline does not wrap; a long query is cut so "A ‘…’ collection." stays one line.
const HEADLINE_QUERY_MAX = 16;
const ASIDE_TITLES_MAX = 3;

export function featuredFromCollection(collection: DiscoverFeaturedCollection): FeaturedBlock {
  return {
    key: collection.id,
    name: collection.name,
    query: collection.query,
    headline: collection.headline,
    subline: 'A hundred possibilities.',
    aside: collection.aside,
    countLabel: `${collection.count}`,
    cards: collection.cards.map((image) => ({ src: discoverImageUrl(image), alt: image.alt })),
    status: 'ready',
  };
}

export function featuredSearching(query: string): FeaturedBlock {
  return {
    ...searchBlockBase(query),
    headline: `Searching ‘${shortQuery(query)}’…`,
    subline: 'One moment.',
    status: 'loading',
  };
}

export function featuredSearchFailed(query: string): FeaturedBlock {
  return {
    ...searchBlockBase(query),
    headline: 'Search didn’t answer.',
    subline: 'Try again.',
    aside: 'Something went wrong on our side. The search is usually back in a moment.',
    status: 'error',
  };
}

// Results are grouped by the query title they matched; the cards are the first seven assets in
// the backend's order, one per asset id.
export function featuredFromSearch(query: string, response: AiSearchResponse | null | undefined): FeaturedBlock {
  const groups = (response?.results ?? []).filter((group) => (group?.assets ?? []).length > 0);
  const seen = new Set<string>();
  const cards: FeaturedCard[] = [];
  let total = 0;

  for (const group of groups) {
    for (const asset of group.assets) {
      if (!asset?.asset_id || seen.has(asset.asset_id)) {
        continue;
      }
      seen.add(asset.asset_id);
      total += 1;
      if (cards.length < FEATURED_CARD_COUNT && asset.portrait_url) {
        cards.push({ src: asset.portrait_url, alt: cardTitle(asset.title, group.title || query) });
      }
    }
  }

  if (total === 0) {
    return {
      ...searchBlockBase(query),
      headline: `Nothing for ‘${shortQuery(query)}’ yet.`,
      subline: 'Ask for it.',
      aside: 'Our artists make new references to order. Send a brief and we confirm the scope and timing before accepting it.',
      status: 'empty',
    };
  }

  const countLabel = total >= DISCOVER_SEARCH_LIMIT ? `${DISCOVER_SEARCH_LIMIT}+` : `${total}`;
  return {
    ...searchBlockBase(query),
    headline: `A ‘${shortQuery(query)}’ collection.`,
    subline: `${countLabel} references.`,
    aside: describeGroups(groups.map((group) => group.title)),
    countLabel,
    cards,
    status: 'ready',
  };
}

function searchBlockBase(query: string): FeaturedBlock {
  return {
    key: `search:${query.toLowerCase()}`,
    name: '',
    query,
    headline: '',
    subline: '',
    aside: '',
    countLabel: '0',
    cards: [],
    status: 'loading',
  };
}

// Some dev assets carry junk titles (one is literally "\\"); those fall back to the group's.
function cardTitle(title: string | undefined, fallback: string): string {
  return title && /[\p{L}\p{N}]/u.test(title) ? title : fallback;
}

function shortQuery(query: string): string {
  return query.length > HEADLINE_QUERY_MAX ? `${query.slice(0, HEADLINE_QUERY_MAX - 1).trimEnd()}…` : query;
}

// "Found in the Florence collection." / "Found in 18 collections: Medieval India, Medieval
// Castle, Medieval Chirch and 15 more." — the titles say what the word actually matched.
function describeGroups(titles: string[]): string {
  const named = titles.filter((title) => !!title);
  if (named.length <= 1) {
    return named[0] ? `Found in the ${named[0]} collection.` : '';
  }

  const shown = named.slice(0, ASIDE_TITLES_MAX).join(', ');
  const rest = named.length - ASIDE_TITLES_MAX;
  return `Found in ${named.length} collections: ${shown}${rest > 0 ? ` and ${rest} more` : ''}.`;
}
